import { z } from 'zod';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { AddressSchema, DateOnlySchema, IdSchema } from '../../domain/entities/common.ts';
import { JobStatusSchema, type Job } from '../../domain/entities/job.ts';
import { JobValidationError, endNotBeforeStart } from './create-job.ts';

/**
 * What can be changed about a job after it exists.
 *
 * Reported from hand-testing as "no obvious way to change a job's status",
 * and it was broader than that: `usecases/jobs/` held only `create-job.ts`,
 * so the name, the address and the dates were as fixed as the status. A
 * detail typed wrongly at creation could only be escaped by abandoning the
 * job and starting another, which takes its schedule and its costs with it.
 *
 * Two fields are deliberately **not** here.
 *
 * **`prefix`.** It is printed into every purchase order and invoice number
 * on the job (`GLN-0001`), and those numbers are held by suppliers and
 * owners outside this system. Changing it would leave one job answering to
 * two references, with no record of when the change happened. It is also
 * refused by the security rules, so this is an invariant rather than a
 * decision the UI is making politely — see `firestore.rules`.
 *
 * **`scheduleOnline`.** Publishing a schedule shows dates to subs and
 * owners who then plan around them, so the rules put it behind `schedule:
 * admin` rather than `jobs: edit`. It has its own control on the schedule
 * page. Including it here would mean anyone who could correct a postcode
 * could also publish a half-built plan — and the write would be refused
 * anyway, taking the postcode with it.
 *
 * `status` needs only `jobs: edit`, including `closed` and `archived`.
 * That was worth asking, by the same reasoning that put `scheduleOnline`
 * behind schedule admin, but closing a job today locks nothing and hides
 * nothing: the status drives the "show closed and archived" filter on the
 * job sidebar and nothing else, and it is reversible in one click. If
 * closing ever starts freezing writes to a job's costs, this is the
 * decision to revisit.
 */
export const UpdateJobInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the job a name').max(200),
    status: JobStatusSchema,
    address: AddressSchema,
    startDate: DateOnlySchema.optional(),
    projectedEndDate: DateOnlySchema.optional(),
    actualEndDate: DateOnlySchema.optional(),
    contractType: z.enum(['fixed', 'openBook']),
    /**
     * Who the customer is (P8-14b).
     *
     * **Plural because a house has two owners often enough**, and the
     * entity has always said so. Narrowing it to one here would be a
     * product decision wearing a convenience.
     *
     * This is the *directory* link, not access: nothing reads it to
     * decide what anybody may see. Which jobs a customer's **member**
     * account can open is `jobIds` on that member (P8-14), and the two
     * are easy to confuse precisely because they both sound like
     * "attach a customer to a job".
     *
     * Optional, so a form that does not offer it cannot blank it. That
     * matters here more than usual: `updateJob` takes every field from
     * the form, and a missing key would otherwise clear the customer on
     * every save from a screen that never showed one.
     */
    ownerContactIds: z.array(IdSchema).max(20).optional(),
  })
  .superRefine(endNotBeforeStart);

export type UpdateJobInput = z.input<typeof UpdateJobInputSchema>;

export interface UpdateJobContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

/**
 * Save changes to a job.
 *
 * **Takes the job the caller loaded, not just its id.** That is the whole
 * point of the version guard here, and getting it wrong is easy: a use case
 * that re-reads the document and guards with what it just read guards
 * against nothing but a collision inside its own microsecond. The lost
 * update that actually happens is longer than that — two people open the
 * form, one saves, the other saves a minute later from a snapshot taken
 * before. Because a form posts *every* field, the second save would put
 * back the old name, the old address and the old dates, silently undoing
 * work its author never saw. Expecting the version the form was built from
 * turns that into a refusal the person can see.
 *
 * (`setScheduleOnline` does re-read, and is right to: it writes one
 * boolean the caller is toggling deliberately, so there is no snapshot of
 * other fields to lose.)
 */
export async function updateJob(
  context: UpdateJobContext,
  job: Job,
  input: UpdateJobInput,
): Promise<Job> {
  const parsed = UpdateJobInputSchema.safeParse(input);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      fieldErrors[issue.path.join('.')] ??= issue.message;
    }
    throw new JobValidationError(fieldErrors);
  }

  const now = context.clock.now().toISOString();
  // Spelled out rather than spread, so adding a field to the entity cannot
  // silently make it editable here.
  const patch = {
    name: parsed.data.name,
    status: parsed.data.status,
    address: parsed.data.address,
    // An explicit `undefined` clears the field: a date entered by mistake
    // has to be removable, and leaving it out of the patch would keep it.
    startDate: parsed.data.startDate,
    projectedEndDate: parsed.data.projectedEndDate,
    actualEndDate: parsed.data.actualEndDate,
    contractType: parsed.data.contractType,
    // Absent leaves it alone; present replaces it. See the schema.
    ...(parsed.data.ownerContactIds
      ? { ownerContactIds: [...new Set(parsed.data.ownerContactIds)] }
      : {}),
    updatedAt: now,
    updatedBy: context.uid,
  };

  await context.data.commit([
    updateWrite<Job>({ companyId: context.companyId }, 'jobs', job.id, patch, expectUnchanged(job)),
  ]);

  return { ...job, ...patch };
}
